// Zustand toast store. Decoupled from React context so any module
// (axios interceptor, route loader, error boundary) can dispatch a toast
// via `useToastStore.getState().push(...)`.

import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastEntry {
  id: string;
  type: ToastType;
  message: string;
  /** Override the type's default duration (ms). */
  duration?: number;
  createdAt: number;
}

interface ToastState {
  toasts: ToastEntry[];
  push: (entry: Omit<ToastEntry, 'id' | 'createdAt'>) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

let counter = 0;
function nextId(): string {
  counter += 1;
  return `toast-${counter}`;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  push: (entry) => {
    const id = nextId();
    const created: ToastEntry = {
      id,
      createdAt: Date.now(),
      ...entry,
    };
    set((state) => {
      // Cap visible stack at 3 — drop oldest first.
      const next = [...state.toasts, created];
      while (next.length > 3) next.shift();
      return { toasts: next };
    });
    return id;
  },

  dismiss: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),

  clear: () => set({ toasts: [] }),
}));
